import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function classNames(...parts) {
  return parts.filter(Boolean).join(' ');
}

export function SelectControl({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  disabled = false,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState({});
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef('');
  const searchTimerRef = useRef(0);
  const reactId = useId();
  const listId = `${id || reactId}-list`;

  const items = [
    { value: '', label: placeholder },
    ...options.map((option) => ({ value: option, label: option })),
  ];
  const selected = items.find((item) => item.value === value) || items[0];
  const selectedIndex = items.findIndex((item) => item.value === value);

  function commit(nextValue) {
    onChange({ target: { name, value: nextValue } });
    setOpen(false);
    triggerRef.current?.focus();
  }

  function positionMenu() {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom - gap - 12;
    const spaceAbove = rect.top - gap - 12;
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow;
    const available = openUp ? spaceAbove : spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 180),
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      maxHeight: Math.min(320, Math.max(160, available)),
    });
  }

  useLayoutEffect(() => {
    if (!open) return undefined;

    positionMenu();
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);

    function onReposition() {
      positionMenu();
    }

    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return undefined;

    function onPointerDown(event) {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }
      setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const active = menuRef.current?.querySelector('[data-active="true"]');
    active?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, open]);

  function moveActive(delta) {
    setActiveIndex((current) => {
      const start = current < 0 ? (delta > 0 ? -1 : items.length) : current;
      return (start + delta + items.length) % items.length;
    });
  }

  function typeahead(key) {
    searchRef.current += key.toLowerCase();
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      searchRef.current = '';
    }, 600);

    const index = items.findIndex((item) => item.label.toLowerCase().startsWith(searchRef.current));
    if (index >= 0) {
      setActiveIndex(index);
      setOpen(true);
    }
  }

  function onTriggerKeyDown(event) {
    if (disabled) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      else moveActive(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
      else moveActive(-1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(items.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      commit((items[activeIndex] || items[0]).value);
      return;
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      return;
    }

    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      typeahead(event.key);
    }
  }

  const activeId = open && activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className={classNames(className, 'select-trigger', open && 'is-open')}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listId}
        aria-activedescendant={activeId}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={classNames('select-trigger-label', !value && 'is-placeholder')}>
          {selected.label}
        </span>
        <span className="select-trigger-caret" aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={listId}
            className="select-menu"
            role="listbox"
            style={menuStyle}
          >
            {items.map((item, index) => (
              <div
                key={`${item.value}-${item.label}`}
                id={`${listId}-opt-${index}`}
                role="option"
                aria-selected={item.value === value}
                data-active={index === activeIndex ? 'true' : undefined}
                className={classNames(
                  'select-option',
                  item.value === value && 'is-selected',
                  index === activeIndex && 'is-active',
                  !item.value && 'is-placeholder'
                )}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => commit(item.value)}
              >
                {item.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
